import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, RotateCcw, ExternalLink, PlayCircle, Megaphone } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { FormField } from '@/components/ui/FormField';
import { Modal } from '@/components/ui/Modal';
import { Switch } from '@/components/ui/Switch';
import { Badge } from '@/components/ui/Badge';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { toast } from '@/store/toastStore';
import { getErrorMessage } from '@/services/apiClient';
import { formatAnnouncementDate } from '@/lib/announcements';
import {
  listAccountAlerts,
  createAccountAlert,
  updateAccountAlert,
  deleteAccountAlert,
  restoreAccountAlert,
  type AccountAlert,
  type AccountAlertInput,
} from '@/services/accountAlerts.service';
import { DatePicker } from '@/components/ui/DatePicker';

const ZERO_DATE = '0000-00-00 00:00:00';

interface FormState {
  alertTitle: string;
  alertContent: string;
  moreDetails: string;
  alertURL: string;
  alertPhoto: string;
  videoURL: string;
  startDate: string; // 'YYYY-MM-DD' or ''
  stopDate: string; // 'YYYY-MM-DD' or ''
  isBroadcast: boolean;
}

/** Plain-text one-liner from stored (possibly HTML) content, for list previews. */
function previewText(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function toDateInput(v: string): string {
  return v && v !== ZERO_DATE ? v.slice(0, 10) : '';
}

function blankForm(): FormState {
  return {
    alertTitle: '',
    alertContent: '',
    moreDetails: '',
    alertURL: '',
    alertPhoto: '',
    videoURL: '',
    startDate: '',
    stopDate: '',
    isBroadcast: false,
  };
}

function formFromAlert(a: AccountAlert): FormState {
  return {
    alertTitle: a.alertTitle,
    alertContent: a.alertContent,
    moreDetails: a.moreDetails,
    alertURL: a.alertURL,
    alertPhoto: a.alertPhoto,
    videoURL: a.videoURL,
    startDate: toDateInput(a.startDate),
    stopDate: toDateInput(a.stopDate),
    isBroadcast: !!a.isBroadcast,
  };
}

function toPayload(form: FormState): AccountAlertInput {
  return {
    alertTitle: form.alertTitle.trim(),
    alertContent: form.alertContent,
    moreDetails: form.moreDetails,
    alertURL: form.alertURL.trim(),
    alertPhoto: form.alertPhoto.trim(),
    videoURL: form.videoURL.trim(),
    startDate: form.startDate ? `${form.startDate} 00:00:00` : ZERO_DATE,
    stopDate: form.stopDate ? `${form.stopDate} 23:59:59` : ZERO_DATE,
    isBroadcast: form.isBroadcast,
  };
}

export function AccountAlertsAdmin() {
  const queryClient = useQueryClient();
  const { isMasterAdmin } = useAuth();
  const [showRemoved, setShowRemoved] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AccountAlert | null>(null);

  const { data: alerts = [], isLoading } = useQuery({
    queryKey: ['admin-account-alerts', showRemoved],
    queryFn: () => listAccountAlerts(showRemoved),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-account-alerts'] });
    // Refresh the dashboard banner + staff archive that read the same data.
    queryClient.invalidateQueries({ queryKey: ['account-alerts'] });
  };

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteAccountAlert(id),
    onSuccess: () => {
      invalidate();
      toast.success('Announcement removed.');
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not remove the announcement.')),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: number) => restoreAccountAlert(id),
    onSuccess: () => {
      invalidate();
      toast.success('Announcement restored.');
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not restore the announcement.')),
  });

  const sorted = useMemo(
    () =>
      alerts
        .slice()
        .sort((a, b) =>
          (b.createDate || b.startDate || '').localeCompare(a.createDate || a.startDate || ''),
        ),
    [alerts],
  );

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (a: AccountAlert) => {
    setEditing(a);
    setFormOpen(true);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden p-6">
      <div className="flex-shrink-0">
        <PageHeader
          title="Announcements"
          actions={
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                Show removed
                <Switch checked={showRemoved} onChange={setShowRemoved} />
              </label>
              <Button onClick={openCreate} leftIcon={<Plus className="h-4 w-4" />}>
                New announcement
              </Button>
            </div>
          }
        />
      </div>

      {isLoading ? (
        <SpinnerOverlay label="Loading announcements…" />
      ) : sorted.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <p className="text-base font-medium text-slate-900 mb-1">No announcements yet</p>
          <p className="text-sm">Create your first announcement to show it on the dashboard.</p>
        </div>
      ) : (
        <>
          <div className="flex-1 min-h-0 border border-slate-200 rounded-t-lg overflow-y-auto">
            <table className="w-full table-fixed">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-2">
                    Announcement
                  </th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-2 w-40">
                    Date
                  </th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-2 w-28">
                    Status
                  </th>
                  <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-3 py-2 w-24">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {sorted.map((a) => {
                  const removed = !a.active;
                  const dateLabel = formatAnnouncementDate(a);
                  const preview = a.alertContent ? previewText(a.alertContent) : '';
                  // Broadcasts are read-only for anyone who isn't a corp admin.
                  const canManage = !a.isBroadcast || isMasterAdmin;
                  const editable = !removed && canManage;
                  return (
                    <tr
                      key={a.accountAlertID}
                      className={`group transition-colors ${removed ? 'bg-amber-50/40' : `hover:bg-slate-50${editable ? ' cursor-pointer' : ''}`}`}
                      onClick={() => {
                        if (editable) openEdit(a);
                      }}
                    >
                      <td className="px-3 py-2.5">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span
                              className={`text-sm font-medium truncate ${removed ? 'text-slate-400' : 'text-slate-900'}`}
                            >
                              {previewText(a.alertTitle) || '(untitled)'}
                            </span>
                            {a.alertURL && (
                              <ExternalLink className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                            )}
                            {a.videoURL && (
                              <PlayCircle className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                            )}
                            {a.isBroadcast && (
                              <Badge variant="purple" size="sm" className="shrink-0">
                                <Megaphone className="h-3 w-3" />
                                All practices
                              </Badge>
                            )}
                          </div>
                          {preview && (
                            <p className="mt-0.5 text-xs text-slate-500 truncate">{preview}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-sm text-slate-600">{dateLabel ?? '—'}</td>
                      <td className="px-3 py-2.5">
                        <Badge variant={removed ? 'warning' : 'success'} size="sm" dot>
                          {removed ? 'Removed' : 'Active'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          {canManage && (removed ? (
                            <Button
                              variant="ghost"
                              size="xs"
                              iconOnly
                              title="Restore"
                              aria-label="Restore announcement"
                              leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
                              loading={restoreMutation.isPending && restoreMutation.variables === a.accountAlertID}
                              onClick={() => restoreMutation.mutate(a.accountAlertID)}
                            />
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="xs"
                                iconOnly
                                title="Edit"
                                aria-label="Edit announcement"
                                leftIcon={<Edit className="h-3.5 w-3.5" />}
                                onClick={() => openEdit(a)}
                              />
                              <Button
                                variant="ghost"
                                size="xs"
                                iconOnly
                                title="Remove"
                                aria-label="Remove announcement"
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                                loading={deleteMutation.isPending && deleteMutation.variables === a.accountAlertID}
                                onClick={() => deleteMutation.mutate(a.accountAlertID)}
                              />
                            </>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex-shrink-0 flex items-center border border-t-0 border-slate-200 rounded-b-lg bg-slate-50 px-4 py-2">
            <span className="text-xs text-slate-500">
              {sorted.length} {sorted.length === 1 ? 'announcement' : 'announcements'}
            </span>
          </div>
        </>
      )}

      {formOpen && (
        <AnnouncementFormModal
          alert={editing}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            setFormOpen(false);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

interface FormModalProps {
  alert: AccountAlert | null;
  onClose: () => void;
  onSaved: () => void;
}

function AnnouncementFormModal({ alert, onClose, onSaved }: FormModalProps) {
  const isEdit = !!alert;
  const { isMasterAdmin } = useAuth();
  const [form, setForm] = useState<FormState>(() => (alert ? formFromAlert(alert) : blankForm()));
  const [saving, setSaving] = useState(false);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.alertTitle.trim()) {
      toast.error('Please enter a title.');
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (isEdit && alert) {
        await updateAccountAlert(alert.accountAlertID, payload);
        toast.success('Announcement updated.');
      } else {
        await createAccountAlert(payload);
        toast.success('Announcement posted.');
      }
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not save the announcement.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit announcement' : 'New announcement'}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Title"
          required
          value={form.alertTitle}
          onChange={(e) => set('alertTitle', e.target.value)}
          placeholder="New features rolled out this week"
        />

        <FormField label="Message">
          <Textarea
            value={form.alertContent}
            onChange={(e) => set('alertContent', e.target.value)}
            placeholder="What would you like to announce?"
            rows={3}
          />
        </FormField>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Link"
            value={form.alertURL}
            onChange={(e) => set('alertURL', e.target.value)}
            placeholder="https://…"
          />
          <Input
            label="Video link"
            value={form.videoURL}
            onChange={(e) => set('videoURL', e.target.value)}
            placeholder="Paste a video link"
          />
        </div>

        <Input
          label="Image"
          value={form.alertPhoto}
          onChange={(e) => set('alertPhoto', e.target.value)}
          placeholder="https://…"
        />

        <FormField label="More details">
          <Textarea
            value={form.moreDetails}
            onChange={(e) => set('moreDetails', e.target.value)}
            placeholder="Extra detail shown when the reader clicks More details."
            rows={4}
          />
        </FormField>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DatePicker
            label="Show from"
            value={form.startDate}
            onChange={(value) => set('startDate', value)}
            clearable
          />
          <DatePicker
            label="Show until"
            value={form.stopDate}
            onChange={(value) => set('stopDate', value)}
            clearable
          />
        </div>

        {!isEdit && isMasterAdmin && (
          <label className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 cursor-pointer select-none">
            <Switch checked={form.isBroadcast} onChange={(v) => set('isBroadcast', v)} />
            <span className="text-sm text-slate-700">
              Post to everyone in this group
              <span className="block text-xs text-slate-500">
                Shows on every practice's dashboard in the group, not just this account.
              </span>
            </span>
          </label>
        )}

        {isEdit && alert?.isBroadcast && (
          <div className="flex items-center gap-2 rounded-md border border-purple-200 bg-purple-50 px-3 py-2 text-sm text-purple-800">
            <Megaphone className="h-4 w-4 shrink-0" />
            This announcement is posted to everyone in your group.
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" loading={saving}>
            {isEdit ? 'Save changes' : 'Post announcement'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
